// apps/web/src/pages/admin/FeedbackAdminPage.tsx

import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  MessageSquare, Search, ChevronLeft, ChevronRight, Loader2, 
  Eye, CheckCircle, XCircle, AlertCircle, Star, Filter, MoreHorizontal
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

type FeedbackType = 'general' | 'bug' | 'feature_request' | 'complaint';
type FeedbackStatus = 'new' | 'reviewing' | 'resolved' | 'dismissed';

interface FeedbackItem {
  id: string;
  name: string;
  email: string;
  type: FeedbackType;
  subject: string;
  message: string;
  rating?: number | null;
  status: FeedbackStatus;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    profile?: {
      fullName?: string | null;
    } | null;
  } | null;
}

const typeLabels: Record<FeedbackType, string> = {
  general: 'General',
  bug: 'Bug Report',
  feature_request: 'Feature Request',
  complaint: 'Complaint',
};

const typeColors: Record<FeedbackType, string> = {
  general: 'bg-blue-500/10 text-blue-500',
  bug: 'bg-red-500/10 text-red-500',
  feature_request: 'bg-green-500/10 text-green-500',
  complaint: 'bg-orange-500/10 text-orange-500',
};

const statusLabels: Record<FeedbackStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const statusColors: Record<FeedbackStatus, string> = {
  new: 'bg-purple-500/10 text-purple-500',
  reviewing: 'bg-yellow-500/10 text-yellow-500',
  resolved: 'bg-green-500/10 text-green-500',
  dismissed: 'bg-gray-500/10 text-gray-500',
};

const FeedbackAdminPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedType, setSelectedType] = useState<FeedbackType | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<FeedbackStatus | undefined>(undefined);
  const [viewingFeedback, setViewingFeedback] = useState<FeedbackItem | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { data, isLoading, error, refetch } = trpc.feedback.getAllFeedback.useQuery({
    page,
    pageSize,
    type: selectedType,
    status: selectedStatus,
  }, {
    refetchInterval: 10000, // Auto refresh every 10 seconds
  });

  const feedbackItems = data?.feedback || [];
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  const updateStatusMutation = trpc.feedback.updateFeedbackStatus.useMutation({
    onSuccess: () => {
      toast.success('Feedback status updated!');
      refetch();
      setIsViewModalOpen(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleStatusUpdate = (feedbackId: string, status: FeedbackStatus) => {
    updateStatusMutation.mutate({ feedbackId, status });
  };

  const handleView = (feedback: FeedbackItem) => {
    setViewingFeedback(feedback);
    setIsViewModalOpen(true);
  };

  const getUserDisplayName = (feedback: FeedbackItem) => {
    if (feedback.user?.profile?.fullName) {
      return feedback.user.profile.fullName;
    }
    return feedback.name;
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-primary" />
            Feedback Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Review and manage user feedback submissions
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filter by Type</label>
              <Select 
                value={selectedType || 'all'} 
                onValueChange={(value) => setSelectedType(value === 'all' ? undefined : value as FeedbackType)}
              >
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="bug">Bug Report</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Filter by Status</label>
              <Select 
                value={selectedStatus || 'all'} 
                onValueChange={(value) => setSelectedStatus(value === 'all' ? undefined : value as FeedbackStatus)}
              >
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="reviewing">Reviewing</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="dismissed">Dismissed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSelectedType(undefined);
                  setSelectedStatus(undefined);
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load feedback. Please try again.
          </AlertDescription>
        </Alert>
      )}

      {/* Feedback Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Feedback ({totalItems})</CardTitle>
          <CardDescription>
            Manage user feedback submissions and update their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : feedbackItems.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No feedback submissions found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbackItems.map((feedback) => (
                    <TableRow key={feedback.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleView(feedback)}>
                      <TableCell>
                        <Badge className={typeColors[feedback.type]}>
                          {typeLabels[feedback.type]}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium max-w-xs truncate">
                        {feedback.subject}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">{getUserDisplayName(feedback)}</div>
                          <div className="text-muted-foreground text-xs">{feedback.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {feedback.rating ? (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm">{feedback.rating}/5</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[feedback.status]}>
                          {statusLabels[feedback.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {format(new Date(feedback.createdAt), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(feedback); }}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {feedback.status !== 'reviewing' && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusUpdate(feedback.id, 'reviewing'); }}>
                                <AlertCircle className="h-4 w-4 mr-2" />
                                Mark as Reviewing
                              </DropdownMenuItem>
                            )}
                            {feedback.status !== 'resolved' && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusUpdate(feedback.id, 'resolved'); }}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Mark as Resolved
                              </DropdownMenuItem>
                            )}
                            {feedback.status !== 'dismissed' && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusUpdate(feedback.id, 'dismissed'); }} className="text-red-600">
                                <XCircle className="h-4 w-4 mr-2" />
                                Dismiss
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <p className="text-sm text-muted-foreground">
                    Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalItems)} of {totalItems}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* View Feedback Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl bg-card text-card-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <Badge className={viewingFeedback ? typeColors[viewingFeedback.type] : ''}>
                {viewingFeedback ? typeLabels[viewingFeedback.type] : ''}
              </Badge>
              <Badge className={viewingFeedback ? statusColors[viewingFeedback.status] : ''}>
                {viewingFeedback ? statusLabels[viewingFeedback.status] : ''}
              </Badge>
            </div>
            <DialogTitle className="text-foreground text-xl">{viewingFeedback?.subject}</DialogTitle>
            <DialogDescription>
              Submitted on {viewingFeedback && format(new Date(viewingFeedback.createdAt), 'MMMM d, yyyy at h:mm a')}
            </DialogDescription>
          </DialogHeader>

          {viewingFeedback && (
            <div className="space-y-6 py-4">
              {/* User Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase mb-3">From</h4>
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name:</span>
                    <span className="font-medium">{getUserDisplayName(viewingFeedback)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email:</span>
                    <span className="font-medium">{viewingFeedback.email}</span>
                  </div>
                  {viewingFeedback.user && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Registered User:</span>
                      <span className="font-medium text-primary">Yes</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rating */}
              {viewingFeedback.rating && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rating:</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`h-5 w-5 ${i < viewingFeedback.rating! ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{viewingFeedback.rating}/5</span>
                </div>
              )}

              {/* Message */}
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground uppercase mb-2">Message</h4>
                <div className="bg-muted/30 rounded-lg p-4 text-foreground whitespace-pre-wrap">
                  {viewingFeedback.message}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {viewingFeedback?.status !== 'resolved' && (
              <Button 
                onClick={() => viewingFeedback && handleStatusUpdate(viewingFeedback.id, 'resolved')}
                disabled={updateStatusMutation.isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {updateStatusMutation.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                Mark Resolved
              </Button>
            )}
            {viewingFeedback?.status !== 'reviewing' && (
              <Button 
                variant="outline"
                onClick={() => viewingFeedback && handleStatusUpdate(viewingFeedback.id, 'reviewing')}
                disabled={updateStatusMutation.isLoading}
              >
                <AlertCircle className="mr-2 h-4 w-4" />
                Mark Reviewing
              </Button>
            )}
            {viewingFeedback?.status !== 'dismissed' && (
              <Button 
                variant="outline"
                onClick={() => viewingFeedback && handleStatusUpdate(viewingFeedback.id, 'dismissed')}
                disabled={updateStatusMutation.isLoading}
                className="text-red-600 hover:text-red-700"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Dismiss
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackAdminPage;
